import { Inject, Injectable, Injector } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { FirebaseError } from 'firebase/app';
import { ToastFacade, UiPromiseService } from 'ngx-zcs-components';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'ngx-core-services';

@Injectable({
  providedIn: 'root',
})
export class RegisterService {

  private get uiPromiseService() {
    return this.injector.get(UiPromiseService);
  }

  private get authenticationService() {
    return this.injector.get(AuthenticationService);
  }

  private get toastFacade() {
    return this.injector.get(ToastFacade);
  }

  constructor(
    @Inject(Injector) private readonly injector: Injector,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    readonly translateService: TranslateService,
  ) {}

  async onRegister(register: NgForm): Promise<void> {
    if (register.invalid) {
      return;
    }
    register.control.markAsPending();
    const { username: displayName, email, password } = register.control.value;
    const { error: authError } = await this.uiPromiseService.run(
      () =>
        this.authenticationService.registration(displayName, email, password),
      {
        loading: true,
        errorHandler: async (error) => {
          const errorMessage = this.authenticationService.errorHandler(
            error as FirebaseError,
          );
          await this.toastFacade.present(
            errorMessage,
            this.translateService.instant('i18n.RegisterService.registrationFailed'),
            {
              type: 'browser',
              severity: 'error',
              browserMode: 'persistent',
            },
          );
          register.reset();
        },
      },
    );
    if (authError) {
      return;
    }
    await this.toastFacade.present(
      this.translateService.instant('i18n.RegisterService.checkEmail'),
      this.translateService.instant('i18n.RegisterService.successfulRegistration'),
      {
        severity: 'success',
        browserMode: 'persistent',
      },
    );
    await this.router.navigate(['../login'], { relativeTo: this.route });
    register.control.reset();
  }
}
